<script lang="ts">
import { computed, defineComponent, inject, ref, unref, watch } from 'vue';

import {
  makeFocusProps,
  makeValidationProps,
  makeInputProps,
  makeDisabledProps,
  makeReadonlyProps,
  useFocus,
  useInput,
  useProxyModel,
  useFilteredInputProps,
  useFilteredInputAttrs,
  makeThemeProps
} from '../../composable';
import { CdsInput, CdsInputLabel, CdsInputDescription } from '../CdsInput';

import { CheckboxGroupKey, checkboxGroupProvideDefaultValue } from './CdsCheckboxGroup.vue';

import type { ICheckboxGroupProvide } from './CdsCheckboxGroup.vue';
import type { IInputProps, IValidationProps } from '../../composable';
import type { PropType } from 'vue';

export const componentName = 'CdsCheckbox';
export default defineComponent({
  name: componentName,
  components: { CdsInput, CdsInputLabel, CdsInputDescription },
  inheritAttrs: false,
  props: {
    /**
     * Промежуточное состояние
     */
    indeterminate: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { boolean }
     */
    'update:modelValue': (value: any) => true,
    /**
     * Событие при изменении промежуточного состояния
     * @type { boolean }
     */
    'update:indeterminate': (value: boolean) => true
  },
  setup(props, { attrs, slots, expose }) {
    const model = useProxyModel(props, 'modelValue');
    const indeterminate = useProxyModel(props, 'indeterminate');
    const { id, hasDescription } = useInput(props, slots, 'checkbox');
    const { isFocused, focus, blur } = useFocus(props, componentName);
    const { inputProps } = useFilteredInputProps<IInputProps & IValidationProps & Record<string, unknown>>(props, [
      'indeterminate',
      'focused'
    ]);
    const { rootAttrs, inputAttrs } = useFilteredInputAttrs(attrs);
    const ariaChecked = computed(() => {
      if (unref(indeterminate)) {
        return unref(model) || 'mixed';
      }
      return unref(model) ? 'true' : 'false';
    });

    const {
      model: groupModel,
      isGroup,
      disabledGroup,
      readonlyGroup,
      errorGroup,
      handleInputCheckbox
    } = inject<ICheckboxGroupProvide>(CheckboxGroupKey, checkboxGroupProvideDefaultValue);

    const validationRef = ref<typeof CdsInput>();

    const internalDisabled = computed(() => unref(disabledGroup) || props.disabled);
    const internalReadonly = computed(() => unref(readonlyGroup) || props.readonly);
    const internalError = computed(() => unref(errorGroup) || props.error);

    const checkboxClasses = computed(() => ({
      'cds-checkbox--disabled': unref(internalDisabled),
      'cds-checkbox--checked': unref(model) || !!attrs.checked,
      'cds-checkbox--indeterminate': unref(indeterminate)
    }));

    watch(
      groupModel,
      (values) => {
        if (Array.isArray(values)) {
          if (props.value) {
            model.value = values.includes(props.value as never);
          }
        }
      },
      { immediate: true }
    );

    function onChange() {
      if (unref(indeterminate)) {
        indeterminate.value = false;
      }
    }

    function onInput(event: Event) {
      model.value = (event.target as HTMLInputElement).checked;

      if (isGroup) {
        handleInputCheckbox({ id: unref(id), checked: unref(model), value: props.value });
      }
    }

    expose({
      id,
      isGroup,
      validationRef
    });

    return {
      id,
      model,
      hasDescription,
      inputProps,
      rootAttrs,
      inputAttrs,
      isFocused,
      ariaChecked,
      internalDisabled,
      internalReadonly,
      internalError,
      isGroup,
      checkboxClasses,
      onBlur: blur,
      onFocus: focus,
      onInput,
      onChange
    };
  }
});
</script>

<template>
  <cds-input
    :id="id"
    ref="validationRef"
    :focused="isFocused"
    class="cds-checkbox"
    :class="checkboxClasses"
    v-bind="{ ...inputProps, ...rootAttrs }"
    :hide-messages="isGroup || inputProps.hideMessages"
  >
    <template #default="{ isDisabled, isReadonly, isValid }">
      <input
        :id="id"
        type="checkbox"
        :name="name"
        :checked="model"
        :disabled="internalDisabled || internalReadonly || isDisabled.value || isReadonly.value"
        :value="value"
        :aria-disabled="internalDisabled || internalReadonly || isDisabled.value || isReadonly.value"
        :aria-checked="ariaChecked"
        :aria-invalid="internalError || isValid.value === false"
        v-bind="{ ...inputAttrs }"
        @blur="onBlur"
        @focus="onFocus"
        @input="onInput"
        @change="onChange"
      />
      <cds-input-label :for="id" :text="label">
        <!--
          @slot Лейбл
          @type string | html
        -->
        <slot name="label" />
      </cds-input-label>
      <cds-input-description v-if="hasDescription" :text="description">
        <!--
          @slot Описание
          @type string | html
        -->
        <slot name="description" />
      </cds-input-description>
    </template>
    <template #messages>
      <!--
        @slot Сообщения и подсказки
        @type string | html
      -->
      <slot name="messages" />
    </template>
  </cds-input>
</template>

<style lang="scss">
@import './CdsCheckbox';
</style>
